import { useEffect, useRef, useState, type MutableRefObject } from 'react'
import { subscribeBroadcast } from '@highlight/ipc'
import { AUDIO_LEVELS_TOPIC, type AudioLevelsSnapshot } from '@shared/audio-levels'

export function useAudioInputLevel(): number {
  const [broadcastLevel, setBroadcastLevel] = useState<number | null>(null)

  useEffect(() => {
    const broadcastApi = typeof window !== 'undefined' ? window.ipc?.broadcast : null
    if (!broadcastApi) {
      return
    }
    return subscribeBroadcast<AudioLevelsSnapshot>(AUDIO_LEVELS_TOPIC, (snapshot) => {
      setBroadcastLevel(snapshot.input)
    })
  }, [])

  return broadcastLevel ?? 0
}

/**
 * Same feed as `useAudioInputLevel`, but re-renders only when the input crosses
 * the idle threshold — the magnitude lands in a ref instead.
 *
 * Levels publish at ~10 Hz for the whole length of a recording. A consumer that
 * only needs "is there sound?" (plus the magnitude read later, inside its own
 * animation loop) would otherwise pay ~10 renders/sec for roughly one visible
 * change per second. Use `useAudioInputLevel` when you genuinely render the
 * continuous value.
 */
export function useAudioInputIdle(idleThreshold = 0.1): {
  isIdle: boolean
  levelRef: MutableRefObject<number>
} {
  const levelRef = useRef(0)
  const [isIdle, setIsIdle] = useState(true)

  useEffect(() => {
    const broadcastApi = typeof window !== 'undefined' ? window.ipc?.broadcast : null
    if (!broadcastApi) {
      return
    }
    return subscribeBroadcast<AudioLevelsSnapshot>(AUDIO_LEVELS_TOPIC, (snapshot) => {
      levelRef.current = snapshot.input
      const nextIdle = snapshot.input < idleThreshold
      // Functional form so an unchanged value bails out without a render.
      setIsIdle((current) => (current === nextIdle ? current : nextIdle))
    })
  }, [idleThreshold])

  return { isIdle, levelRef }
}
